import L from "leaflet";
import type { FeatureCollection, Feature, Polygon } from "geojson";
import { LAYER_REGISTRY } from "./registry";
import type { LayerName, MeasureId } from "./types";

export type ChoroplethLayerOptions = {
  map: L.Map;
  region: string;
  layer: LayerName;
  measure: MeasureId;
  geometry: FeatureCollection;
  values: Map<string, { value: number | null; binIndex: number | null }>;
  onReady?: () => void;
};

type FeatureRecord = {
  daUid: string;
  centroidX: number;
  bin: number | null;
  ring: [number, number][];
};

export class ChoroplethLayer {
  private opts: ChoroplethLayerOptions;
  private canvas: HTMLCanvasElement | null = null;
  private offscreen: HTMLCanvasElement | null = null;
  private order: FeatureRecord[] = [];
  private readonly onMoveEnd = (): void => this.repaint();
  private readonly onZoomEnd = (): void => this.repaint();
  private readonly onViewReset = (): void => this.repaint();
  private readonly onResize = (): void => this.repaint();

  constructor(opts: ChoroplethLayerOptions) {
    this.opts = opts;
  }

  async mount(): Promise<void> {
    const { map } = this.opts;
    this.canvas = document.createElement("canvas");
    this.canvas.style.opacity = "0.8";
    map.getPanes().overlayPane.appendChild(this.canvas);

    this.rebuild();

    map.on("moveend", this.onMoveEnd);
    map.on("zoomend", this.onZoomEnd);
    map.on("viewreset", this.onViewReset);
    map.on("resize", this.onResize);

    this.paintFull();
    this.opts.onReady?.();
  }

  repaint(): void {
    if (this.canvas === null || this.offscreen === null) return;
    this.rebuild();
    this.paintFull();
  }

  unmount(): void {
    const { map } = this.opts;
    map.off("moveend", this.onMoveEnd);
    map.off("zoomend", this.onZoomEnd);
    map.off("viewreset", this.onViewReset);
    map.off("resize", this.onResize);
    if (this.canvas?.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    this.canvas = null;
    this.offscreen = null;
    this.order = [];
  }

  // Re-project every feature to container-relative pixels, re-anchor the
  // canvas inside overlayPane so its origin sits at the current layer-space
  // location of container (0,0), and re-draw the off-screen prerender.
  // Called on mount and on every pan/zoom/resize repaint.
  private rebuild(): void {
    const { map, geometry } = this.opts;
    const size = map.getSize();
    if (!this.canvas) return;
    this.canvas.width = size.x;
    this.canvas.height = size.y;
    // Re-anchor the canvas to overlayPane's current layer origin so it
    // moves in lockstep with the pane and its transform after Leaflet
    // resets the transform on moveend/zoomend.
    L.DomUtil.setPosition(this.canvas, map.containerPointToLayerPoint([0, 0]));

    // Build feature order sorted by centroid X in container pixels.
    const records: FeatureRecord[] = [];
    for (const feature of geometry.features as Feature<Polygon>[]) {
      const daUid = String((feature.properties as any).da_uid);
      const ring = (feature.geometry.coordinates[0] as [number, number][]).map(
        ([lng, lat]) => {
          const p = map.latLngToContainerPoint(L.latLng(lat, lng));
          return [p.x, p.y] as [number, number];
        }
      );
      const centroidX = ring.reduce((s, [x]) => s + x, 0) / ring.length;
      const entry = this.opts.values.get(daUid);
      records.push({ daUid, centroidX, bin: entry?.binIndex ?? null, ring });
    }
    records.sort((a, b) => a.centroidX - b.centroidX);
    this.order = records;

    // Pre-render off-screen.
    this.offscreen = document.createElement("canvas");
    this.offscreen.width = size.x;
    this.offscreen.height = size.y;
    const octx = this.offscreen.getContext("2d")!;
    const rampVars = LAYER_REGISTRY[this.opts.layer].rampTokens;
    const rampValues = rampVars.map((v) =>
      getComputedStyle(document.documentElement).getPropertyValue(v).trim() || "#666"
    );
    for (const rec of records) {
      if (rec.bin === null) continue;
      octx.beginPath();
      const [x0, y0] = rec.ring[0]!;
      octx.moveTo(x0, y0);
      for (let i = 1; i < rec.ring.length; i++) {
        const [x, y] = rec.ring[i]!;
        octx.lineTo(x, y);
      }
      octx.closePath();
      octx.fillStyle = rampValues[rec.bin] ?? "#666";
      octx.fill();
    }
  }

  setValues(values: Map<string, { value: number | null; binIndex: number | null }>): void {
    this.opts.values = values;
    this.refresh();
  }

  refresh(): void {
    if (!this.opts.map) return;
    void this.mount();
  }

  private paintFull(): void {
    if (!this.canvas || !this.offscreen) return;
    const ctx = this.canvas.getContext("2d")!;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.offscreen, 0, 0);
  }
}

export function __test_getPaintOrder(layer: ChoroplethLayer): string[] {
  return (layer as any).order.map((r: FeatureRecord) => r.daUid);
}
